import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Button, Input, Popconfirm, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useStatusTrackerV2 } from "../hooks/useStatusTrackerV2";
import { selectCurrentCells } from "../state/selectors";
import { coachFlagsOnCell, selectCoachFlags } from "../mappers/coachTag.mapper";
import { FlagTag } from "../views/FlagTag";

const { Text } = Typography;
const { TextArea } = Input;

// Popover body for tagging one cell. Only coach flags can be tagged by hand — the backend
// rejects a logic flag id, so they never reach the list. A flag already on the cell loads its
// note when picked, so saving edits the existing tag rather than adding beside it.
export const CoachFlagTagPopover = ({ uniqueKey, onDone }) => {
    const { state, team, actions } = useStatusTrackerV2();
    const flags = useSelector(({ userDefinedFlags }) => userDefinedFlags?.flags || []);
    const coachFlags = selectCoachFlags(flags);

    // Which cell this is. Resolved here rather than in DateCell so the lookup runs once, when
    // the popover opens, instead of on every cell render. Same split as CellDetailModal's title.
    const [playerId, dateStr] = uniqueKey.split("_");
    const player = team.players.find((p) => p.player_id === Number(playerId));
    const playerName = player ? `${player.first_name} ${player.last_name}` : "";

    // What this cell already carries, as { flagId: note }. Captured once: `existing`
    // re-derives from the grid, which a save patches.
    const existing = coachFlagsOnCell(selectCurrentCells(state)[uniqueKey]);
    const [tagged] = useState(() =>
        Object.fromEntries(existing.map((f) => [f.user_defined_flag_id, f.notes || ""]))
    );
    const noteFor = (id) => tagged[id] ?? "";

    // With exactly one coach flag on the cell, the likely intent is to edit it.
    const taggedIds = Object.keys(tagged);
    const initialFlagId = taggedIds.length === 1 ? Number(taggedIds[0]) : null;

    const [flagId, setFlagId] = useState(initialFlagId);
    const [notes, setNotes] = useState(() => noteFor(initialFlagId));
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const isRetag = flagId != null && flagId in tagged;
    // Dirty means saving would change what is stored: a flag the cell does not carry yet, or a
    // different note on one it does. Picking a flag it already carries is not itself a change —
    // which is why a cell with two flags must compare against the stored note, not against the
    // empty selection the popover opened with.
    const isDirty = flagId != null && (isRetag ? notes !== tagged[flagId] : true);

    const handleFlagChange = (id) => {
        setFlagId(id);
        setNotes(noteFor(id));
    };

    const handleSave = async () => {
        const flag = coachFlags.find((f) => f.id === flagId);
        if (!flag) return;
        setSaving(true);
        const saved = await actions.saveCoachFlagTag({ uniqueKey, flag, notes });
        setSaving(false);
        if (saved) onDone();
    };

    const handleDelete = async () => {
        setDeleting(true);
        const removed = await actions.deleteCoachFlagTag({ uniqueKey, flagId });
        setDeleting(false);
        if (removed) onDone();
    };

    if (coachFlags.length === 0) {
        return (
            <div className="stv2-tag-popover">
            <div className="stv2-tag-popover-header">
                <Text strong>{playerName}</Text>
                <Text type="secondary">{dayjs(dateStr).format("ddd, D MMM YYYY")}</Text>
            </div>
                <Text type="secondary">
                    No coach flags yet. Create one in Settings to tag players by hand.
                </Text>
            </div>
        );
    }

    return (
        <div className="stv2-tag-popover">
            <div className="stv2-tag-popover-header">
                <Text strong>{playerName}</Text>
                <Text type="secondary">{dayjs(dateStr).format("ddd, D MMM YYYY")}</Text>
            </div>
            <Text strong>Flag</Text>
            <div className="stv2-flag-tags">
                {coachFlags.map((f) => (
                    <FlagTag key={f.id} flag={f} selected={flagId === f.id} onSelect={handleFlagChange} />
                ))}
            </div>
            {isRetag && (
                <Text type="secondary" className="stv2-tag-popover-hint">
                    Already tagged on this date — saving updates the note.
                </Text>
            )}
            <Text strong>Notes</Text>
            <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional"
                rows={3}
                maxLength={5000}
            />
            <div className="stv2-tag-popover-actions">
                {/* Only a flag actually on this cell can be removed from it. */}
                {isRetag && (
                    <Popconfirm
                        title="Remove this flag?"
                        description="It will no longer be on this player for this date."
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                        cancelText="Keep"
                        onConfirm={handleDelete}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleting}
                            disabled={saving}
                            className="stv2-tag-popover-delete"
                        >
                            Remove
                        </Button>
                    </Popconfirm>
                )}
                <Button size="small" onClick={onDone} disabled={saving || deleting}>
                    Cancel
                </Button>
                <Button
                    size="small"
                    type="primary"
                    onClick={handleSave}
                    loading={saving}
                    disabled={flagId == null || deleting}
                    className={flagId != null && !saving && isDirty ? "pulse" : ""}
                >
                    {isRetag ? "Update" : "Save"}
                </Button>
            </div>
        </div>
    );
};
